export const NEW_MESSAGE_WINDOW = 'new-message';
/** sessionStorage key for the saved window layout (see useChatWindowPersistence). */
export const CHAT_WINDOWS_STORAGE_KEY = 'roger.chat.windows';
export const MAX_MINIMIZED = 8;

export interface ChatWindowsState {
  dockOpen: boolean;
  /** Index 0 sits next to the dock. */
  open: string[];
  /** Index 0 is the top of the tab stack. */
  minimized: string[];
  expanded: string | null;
  /** 1 or 2; the sheet uses 1. */
  maxOpen: 1 | 2;
}

export const initialChatWindows: ChatWindowsState = {
  dockOpen: false,
  open: [],
  minimized: [],
  expanded: null,
  maxOpen: 2,
};

/** Minimize from the far end while too many are open, then cap the tab stack. */
function overflow(s: ChatWindowsState): ChatWindowsState {
  const open = [...s.open];
  let minimized = [...s.minimized];
  while (open.length > s.maxOpen) {
    minimized = [open.pop() as string, ...minimized];
  }
  return { ...s, open, minimized: minimized.slice(0, MAX_MINIMIZED) };
}

export function openWindow(s: ChatWindowsState, id: string): ChatWindowsState {
  return overflow({
    ...s,
    open: [id, ...s.open.filter(x => x !== id)],
    minimized: s.minimized.filter(x => x !== id),
    expanded: s.expanded === id ? id : null,
  });
}

export function minimizeWindow(
  s: ChatWindowsState,
  id: string
): ChatWindowsState {
  return overflow({
    ...s,
    open: s.open.filter(x => x !== id),
    minimized: [id, ...s.minimized.filter(x => x !== id)],
    expanded: s.expanded === id ? null : s.expanded,
  });
}

export function closeWindow(s: ChatWindowsState, id: string): ChatWindowsState {
  return {
    ...s,
    open: s.open.filter(x => x !== id),
    minimized: s.minimized.filter(x => x !== id),
    expanded: s.expanded === id ? null : s.expanded,
  };
}

export function toggleExpanded(
  s: ChatWindowsState,
  id: string
): ChatWindowsState {
  if (s.expanded === id) return { ...s, expanded: null };
  const others = s.open.filter(x => x !== id);
  return overflow({
    ...s,
    open: [id],
    minimized: [
      ...others,
      ...s.minimized.filter(x => x !== id && !others.includes(x)),
    ],
    expanded: id,
  });
}

export function setMaxOpen(
  s: ChatWindowsState,
  maxOpen: 1 | 2
): ChatWindowsState {
  return overflow({ ...s, maxOpen, expanded: maxOpen < 2 ? null : s.expanded });
}

export function replaceWindow(
  s: ChatWindowsState,
  fromId: string,
  toId: string
): ChatWindowsState {
  // A minimized source is replaced where it sits, as a tab; if the target is
  // already open the tab just goes.
  if (s.minimized.includes(fromId))
    return {
      ...s,
      minimized: s.open.includes(toId)
        ? s.minimized.filter(x => x !== fromId)
        : s.minimized
            .map(x => (x === fromId ? toId : x))
            .filter((x, i, all) => all.indexOf(x) === i),
    };
  if (!s.open.includes(fromId)) return openWindow(s, toId);
  return {
    ...s,
    open: s.open
      .map(x => (x === fromId ? toId : x))
      .filter((x, i, all) => x !== toId || all.indexOf(x) === i),
    minimized: s.minimized.filter(x => x !== toId),
    expanded: s.expanded === fromId ? toId : s.expanded,
  };
}

export function maxOpenWindowsFor(viewportWidth: number): 1 | 2 {
  return viewportWidth < 1280 ? 1 : 2;
}
